import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { mainSiteUrl } from '@/mocks/categories';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://kakobuyfinds.blog';

export interface ArticleContent {
  id: string;
  slug: string;
  title: string;
  metaTitle: string;
  metaDescription: string;
  keywords: string;
  publishedDate: string;
  readTime: string;
  author: string;
  category: string;
  excerpt: string;
  tags: string[];
  sections: { heading: string; content: string }[];
  faqs: { question: string; answer: string }[];
  internalLinks: { text: string; href: string; external?: boolean }[];
  cardImage: string;
  cardImageAlt: string;
  cardImageTitle: string;
  heroImage: string;
  heroImageAlt: string;
  heroImageTitle: string;
}

interface ArticleLayoutProps {
  content: ArticleContent;
}

export default function ArticleLayout({ content }: ArticleLayoutProps) {
  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: content.title,
    description: content.metaDescription,
    datePublished: content.publishedDate,
    author: {
      '@type': 'Organization',
      name: content.author,
    },
    publisher: {
      '@type': 'Organization',
      name: 'KakobuyFinds.blog',
      logo: {
        '@type': 'ImageObject',
        url: 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/9f7209ca9e1d80750ccbea8c559f256f.png',
      },
    },
    image: content.heroImage,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${siteUrl}/${content.slug}`,
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: content.faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <Navbar />

      <main>
        <article className="pt-20 md:pt-24">
          <div className="w-full px-4 md:px-6 lg:px-10 py-10 md:py-16 bg-background-50">
            <div className="max-w-3xl mx-auto">
              <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-6 flex-wrap" aria-label="Breadcrumb">
                <Link to="/" className="hover:text-primary-600 transition-colors duration-200 cursor-pointer">Home</Link>
                <i className="ri-arrow-right-s-line text-xs"></i>
                <span className="text-foreground-400">Shopping Guides</span>
                <i className="ri-arrow-right-s-line text-xs"></i>
                <span className="text-foreground-700 font-medium truncate">{content.title}</span>
              </nav>

              <span className="inline-block text-[11px] font-semibold text-accent-700 bg-accent-100/80 px-3 py-1 rounded-md uppercase tracking-wider mb-4">
                {content.category}
              </span>
              <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-extrabold text-foreground-900 leading-tight">
                {content.title}
              </h1>

              <div className="flex flex-wrap items-center gap-4 mt-6 text-sm text-foreground-500">
                <span className="flex items-center gap-1.5">
                  <span className="w-4 h-4 flex items-center justify-center"><i className="ri-calendar-line text-sm"></i></span>
                  {content.publishedDate}
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="w-4 h-4 flex items-center justify-center"><i className="ri-time-line text-sm"></i></span>
                  {content.readTime}
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="w-4 h-4 flex items-center justify-center"><i className="ri-user-line text-sm"></i></span>
                  {content.author}
                </span>
              </div>

              <div className="flex flex-wrap gap-2 mt-6">
                {content.internalLinks.map((link) => (
                  link.external ? (
                    <a
                      key={link.text}
                      href={link.href}
                      target="_blank"
                      rel="noopener nofollow"
                      className="text-xs text-accent-700 bg-accent-100/80 hover:bg-accent-200 px-3 py-1.5 rounded-full transition-colors duration-200 cursor-pointer"
                    >
                      {link.text} <i className="ri-external-link-line text-[10px] ml-0.5"></i>
                    </a>
                  ) : (
                    <Link
                      key={link.text}
                      to={link.href}
                      className="text-xs text-accent-700 bg-accent-100/80 hover:bg-accent-200 px-3 py-1.5 rounded-full transition-colors duration-200 cursor-pointer"
                    >
                      {link.text}
                    </Link>
                  )
                ))}
              </div>
            </div>
          </div>

          <div className="w-full px-4 md:px-6 lg:px-10 py-8 md:py-12 bg-background-100">
            <div className="max-w-3xl mx-auto">
              {content.heroImage && (
                <div className="w-full h-[280px] md:h-[360px] rounded-2xl overflow-hidden mb-10">
                  <img
                    src={content.heroImage}
                    alt={content.heroImageAlt}
                    title={content.heroImageTitle}
                    className="w-full h-full object-cover object-center"
                  />
                </div>
              )}

              <div className="space-y-10">
                {content.sections.map((section, idx) => (
                  <section key={idx} className="scroll-mt-24">
                    <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4">
                      {section.heading}
                    </h2>
                    <p className="text-base text-foreground-700 leading-[1.8] whitespace-pre-line">
                      {section.content}
                    </p>
                  </section>
                ))}
              </div>

              <div className="mt-16 p-6 md:p-8 bg-background-50 rounded-2xl border border-background-200/60">
                <h2 className="font-heading text-2xl font-bold text-foreground-900 mb-6">
                  Frequently Asked Questions
                </h2>
                <div className="space-y-6">
                  {content.faqs.map((faq, idx) => (
                    <details key={idx} className="group">
                      <summary className="flex items-center justify-between cursor-pointer list-none">
                        <h3 className="text-base font-semibold text-foreground-900 pr-4">{faq.question}</h3>
                        <span className="w-6 h-6 flex items-center justify-center rounded-full bg-accent-100 text-accent-700 flex-shrink-0 group-open:rotate-180 transition-transform duration-200">
                          <i className="ri-arrow-down-s-line text-sm"></i>
                        </span>
                      </summary>
                      <p className="text-sm text-foreground-600 leading-relaxed mt-3 pl-1">{faq.answer}</p>
                    </details>
                  ))}
                </div>
              </div>

              <div className="mt-12 p-6 md:p-8 bg-primary-50 rounded-2xl border border-primary-200/60 text-center">
                <h3 className="font-heading text-xl font-bold text-foreground-900 mb-3">
                  Ready To Start Shopping?
                </h3>
                <p className="text-sm text-foreground-600 mb-6 max-w-md mx-auto">
                  Access the complete Kakobuy Spreadsheet and discover thousands of curated product finds across all categories.
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                  <a
                    href={mainSiteUrl}
                    target="_blank"
                    rel="noopener nofollow"
                    className="px-6 py-3 text-sm font-semibold text-background-50 bg-primary-500 rounded-full hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
                  >
                    Open Kakobuy Sheet
                  </a>
                  <Link
                    to="/"
                    className="px-6 py-3 text-sm font-semibold text-foreground-900 bg-background-200/70 rounded-full hover:bg-background-300 transition-colors duration-200 whitespace-nowrap cursor-pointer"
                  >
                    Back To Home
                  </Link>
                </div>
              </div>

              <nav className="mt-12 pt-8 border-t border-background-200/60">
                <h4 className="text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-4">
                  Related Resources
                </h4>
                <div className="flex flex-wrap gap-3">
                  {content.internalLinks.map((link) => (
                    link.external ? (
                      <a
                        key={link.text}
                        href={link.href}
                        target="_blank"
                        rel="noopener nofollow"
                        className="text-sm text-foreground-600 hover:text-primary-600 transition-colors duration-200 cursor-pointer flex items-center gap-1"
                      >
                        {link.text}
                        <i className="ri-arrow-right-up-line text-xs"></i>
                      </a>
                    ) : (
                      <Link
                        key={link.text}
                        to={link.href}
                        className="text-sm text-foreground-600 hover:text-primary-600 transition-colors duration-200 cursor-pointer"
                      >
                        {link.text}
                      </Link>
                    )
                  ))}
                  <Link
                    to="/"
                    className="text-sm font-semibold text-primary-600 hover:text-primary-700 transition-colors duration-200 cursor-pointer"
                  >
                    &larr; Back to Homepage
                  </Link>
                </div>
              </nav>
            </div>
          </div>
        </article>
      </main>

      <Footer />
    </>
  );
}